import { ArrowRight, CalendarDays, Check, MessageSquare } from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform, type Variants } from "motion/react";
import { type ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createStaggerContainer, createStaggerItem, REVEAL_EASE } from "@/src/common/components/animated-section";
import { MockAvatar } from "@/src/common/components/app-mock/avatar";
import { MobileEventsScreen, MobileFeedScreen, MobileVideosScreen } from "@/src/common/components/app-mock/mobile-screens";
import { getPerson } from "@/src/common/components/app-mock/mock-community";
import { CtaAnchor } from "@/src/common/components/cta-link";
import { EyebrowBadge } from "@/src/common/components/eyebrow-badge";
import { PhoneFrame } from "@/src/common/components/phone-frame";
import { APP_LINKS } from "@/src/common/constants/app-links";
import { env } from "@/src/common/constants/env";
import type { EntranceVariant } from "@/src/common/hooks/use-entrance";
import { useMountEntrance } from "@/src/common/hooks/use-mount-entrance";

const HEADLINE_KEYS = ["hero.title_1", "hero.title_2", "hero.title_3"] as const;
const BULLET_KEYS = ["bullet_1", "bullet_2", "bullet_3"] as const;

const PHONE_SPRING = { type: "spring", stiffness: 110, damping: 18, mass: 0.9 } as const;

interface FloatingCard {
	id: string;
	className: string;
	delay: number;
	floatDuration: number;
	icon: ReactNode;
	title: string;
	subtitle: string;
}

const MAYA = getPerson("m1");

const FLOATING_CARDS: ReadonlyArray<FloatingCard> = [
	{
		id: "joined",
		className: "left-[10%] top-[8%]",
		delay: 1.1,
		floatDuration: 7,
		icon: <MockAvatar person={MAYA} size={28} presence="online" />,
		title: "Maya Kim just joined",
		subtitle: "Cohort 4 · 2 min ago",
	},
	{
		id: "rsvp",
		className: "right-[8%] top-[34%]",
		delay: 1.3,
		floatDuration: 8,
		icon: (
			<span className="inline-flex size-7 items-center justify-center rounded-full bg-brand-100 text-brand-700">
				<CalendarDays className="size-3.5" />
			</span>
		),
		title: "Fall Job Fair",
		subtitle: "128 RSVPs · 14 employers",
	},
	{
		id: "message",
		className: "left-[12%] bottom-[24%]",
		delay: 1.5,
		floatDuration: 6.5,
		icon: (
			<span className="inline-flex size-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
				<MessageSquare className="size-3.5" />
			</span>
		),
		title: "New message from Devon",
		subtitle: "See you on the training floor at 9?",
	},
];

function createLineVariants(reduced: boolean): Variants {
	return {
		hidden: { y: reduced ? 0 : "110%", opacity: reduced ? 1 : 0, transition: { duration: 0 } },
		visible: (index: number) => ({
			y: 0,
			opacity: 1,
			transition: reduced ? { duration: 0 } : { duration: 0.8, ease: REVEAL_EASE, delay: 0.1 + index * 0.08 },
		}),
	};
}

function createPhoneVariants(reduced: boolean, rotate: number, delay: number): Variants {
	return {
		hidden: {
			opacity: reduced ? 1 : 0,
			y: reduced ? 0 : 60,
			rotate: reduced ? 0 : rotate,
			scale: reduced ? 1 : 0.94,
			transition: { duration: 0 },
		},
		visible: { opacity: 1, y: 0, rotate: 0, scale: 1, transition: reduced ? { duration: 0 } : { ...PHONE_SPRING, delay } },
	};
}

function createCardVariants(reduced: boolean, delay: number): Variants {
	return {
		hidden: { opacity: reduced ? 1 : 0, y: reduced ? 0 : 14, transition: { duration: 0 } },
		visible: { opacity: 1, y: 0, transition: reduced ? { duration: 0 } : { duration: 0.6, ease: REVEAL_EASE, delay } },
	};
}

function HeroBackdrop({ reduced }: { reduced: boolean }) {
	return (
		<>
			<div aria-hidden="true" className="absolute inset-0 bg-grid opacity-50" />
			<div
				aria-hidden="true"
				className="pointer-events-none absolute -top-48 left-1/2 h-[40rem] w-[64rem] -translate-x-1/2 rounded-full"
				style={{ background: "radial-gradient(closest-side, oklch(0.62 0.20 254 / 0.38), transparent)" }}
			/>
			<motion.div
				aria-hidden="true"
				className="pointer-events-none absolute left-1/2 top-16 size-48 -translate-x-1/2 rounded-full"
				style={{ background: "radial-gradient(closest-side, oklch(0.82 0.13 240 / 0.6), transparent)" }}
				initial={{ opacity: 0.5 }}
				animate={reduced ? { opacity: 0.6 } : { opacity: [0.5, 0.8, 0.5] }}
				transition={reduced ? { duration: 0 } : { duration: 5, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
			/>
		</>
	);
}

interface FloatingActivityCardProps {
	card: FloatingCard;
	reduced: boolean;
	entrance: EntranceVariant;
}

function FloatingActivityCard({ card, reduced, entrance }: FloatingActivityCardProps) {
	return (
		<motion.div
			aria-hidden="true"
			className={`absolute z-20 hidden lg:block ${card.className}`}
			initial={false}
			animate={entrance}
			variants={createCardVariants(reduced, card.delay)}
		>
			<motion.div
				className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/95 px-3.5 py-2.5 shadow-lift"
				animate={reduced ? undefined : { y: [0, -6, 0] }}
				transition={
					reduced ? undefined : { duration: card.floatDuration, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut", delay: card.delay }
				}
			>
				{card.icon}
				<span className="flex flex-col">
					<span className="text-xs font-semibold text-neutral-900">{card.title}</span>
					<span className="text-[11px] text-neutral-500">{card.subtitle}</span>
				</span>
			</motion.div>
		</motion.div>
	);
}

export function HeroSection() {
	const { t } = useTranslation("home");
	const reduced = useReducedMotion() === true;
	const entrance = useMountEntrance(reduced);
	const stageRef = useRef<HTMLDivElement>(null);
	const { scrollYProgress } = useScroll({ target: stageRef, offset: ["start end", "end start"] });
	const centerY = useTransform(scrollYProgress, [0, 1], [-24, 24]);
	const sideY = useTransform(scrollYProgress, [0, 1], [40, -40]);

	const container = createStaggerContainer(reduced, 0.08, 0.05);
	const item = createStaggerItem(reduced);
	const line = createLineVariants(reduced);

	return (
		<section className="bg-dark-hero relative isolate overflow-hidden pt-20 pb-32 md:pt-28 md:pb-40">
			<HeroBackdrop reduced={reduced} />

			<div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
				<motion.div className="flex flex-col items-center gap-7 text-center" initial={false} animate={entrance} variants={container}>
					<motion.div variants={item}>
						<EyebrowBadge tone="white" icon={<span className="size-1.5 animate-pulse rounded-full bg-emerald-400" />}>
							{t("hero.eyebrow")}
						</EyebrowBadge>
					</motion.div>

					<h1 className="text-balance text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl">
						{HEADLINE_KEYS.map((key, index) => (
							<span key={key} className="-mb-[0.15em] block overflow-hidden pb-[0.15em]">
								<motion.span
									custom={index}
									variants={line}
									className={index === HEADLINE_KEYS.length - 1 ? "text-gradient-hero text-gradient-animated block" : "block"}
								>
									{t(key)}
								</motion.span>
							</span>
						))}
					</h1>

					<motion.p variants={item} className="max-w-2xl text-balance text-base text-white/70 sm:text-lg">
						{t("hero.subtitle")}
					</motion.p>

					<motion.div variants={item} className="flex flex-col items-center gap-3 sm:flex-row">
						<CtaAnchor href={APP_LINKS.SIGN_UP} variant="gradient" size="lg" endContent={<ArrowRight className="h-4 w-4" />}>
							{t("hero.cta_primary")}
						</CtaAnchor>
						<CtaAnchor href={env.CALENDLY_URL} target="_blank" rel="noreferrer" variant="outline-dark" size="lg">
							{t("hero.cta_secondary")}
						</CtaAnchor>
					</motion.div>

					<motion.ul
						variants={item}
						className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/60 sm:text-sm"
					>
						{BULLET_KEYS.map((key) => (
							<li key={key} className="inline-flex items-center gap-1.5">
								<Check className="h-3.5 w-3.5 text-emerald-400" />
								{t(`hero.${key}`)}
							</li>
						))}
					</motion.ul>
				</motion.div>

				<div ref={stageRef} className="relative mx-auto mt-16 hidden h-[700px] max-w-5xl items-end justify-center md:flex">
					<div
						aria-hidden="true"
						className="absolute inset-x-0 bottom-0 z-30 h-32 bg-gradient-to-t from-[oklch(0.18_0.04_254)] via-transparent to-transparent"
					/>

					{FLOATING_CARDS.map((card) => (
						<FloatingActivityCard key={card.id} card={card} reduced={reduced} entrance={entrance} />
					))}

					<div className="absolute left-1/2 top-12 -translate-x-[170%] scale-90 opacity-90">
						<motion.div style={reduced ? undefined : { y: sideY }}>
							<motion.div initial={false} animate={entrance} variants={createPhoneVariants(reduced, -10, 0.55)}>
								<PhoneFrame tilt="left" size="sm" label={t("hero.frames.events")}>
									<MobileEventsScreen />
								</PhoneFrame>
							</motion.div>
						</motion.div>
					</div>
					<motion.div className="relative z-10" style={reduced ? undefined : { y: centerY }}>
						<motion.div initial={false} animate={entrance} variants={createPhoneVariants(reduced, 0, 0.4)}>
							<PhoneFrame tilt="center" size="md" label={t("hero.frames.feed")}>
								<MobileFeedScreen />
							</PhoneFrame>
						</motion.div>
					</motion.div>
					<div className="absolute right-1/2 top-12 translate-x-[170%] scale-90 opacity-90">
						<motion.div style={reduced ? undefined : { y: sideY }}>
							<motion.div initial={false} animate={entrance} variants={createPhoneVariants(reduced, 10, 0.7)}>
								<PhoneFrame tilt="right" size="sm" label={t("hero.frames.clips")}>
									<MobileVideosScreen />
								</PhoneFrame>
							</motion.div>
						</motion.div>
					</div>
				</div>

				<motion.div
					initial={false}
					animate={entrance}
					variants={createPhoneVariants(reduced, 0, 0.4)}
					className="mx-auto mt-12 flex max-w-sm justify-center md:hidden"
				>
					<PhoneFrame tilt="center" size="sm" label={t("hero.frames.feed")}>
						<MobileFeedScreen />
					</PhoneFrame>
				</motion.div>
			</div>
		</section>
	);
}
